import { t } from '../i18n';
import { isTouchDevice } from '../device';
import type { TouchInput } from '../game/player';
import type { XpProfile } from '../api/types';

/** DOM-based HUD (native RTL/Arabic text — Blueprint §5.1). */
export class Hud {
  private districtChip = document.getElementById('district-chip')!;
  private perfChip = document.getElementById('perf-chip')!;
  private prompt = document.getElementById('interact-prompt')!;
  private toast = document.getElementById('xp-toast')!;
  private interactBtn = document.getElementById('interact-btn') as HTMLButtonElement;
  private mapBtn = document.getElementById('map-btn') as HTMLButtonElement;
  private viewBtn = document.getElementById('view-btn') as HTMLButtonElement;
  private helpBtn = document.getElementById('help-btn') as HTMLButtonElement;
  private muteBtn = document.getElementById('mute-btn') as HTMLButtonElement;
  private controlsPanel = document.getElementById('controls-panel')!;

  private frames = 0;
  private fpsTimer = 0;
  private toastTimer: number | null = null;

  onInteractPressed: (() => void) | null = null;
  onMapPressed: (() => void) | null = null;
  onViewPressed: (() => void) | null = null;
  onZoomPressed: ((steps: number) => void) | null = null;
  onMutePressed: (() => void) | null = null;

  constructor() {
    this.interactBtn.addEventListener('click', () => this.onInteractPressed?.());
    this.mapBtn.textContent = t('map_button');
    this.mapBtn.addEventListener('click', () => this.onMapPressed?.());
    this.viewBtn.addEventListener('click', () => this.onViewPressed?.());

    this.helpBtn.textContent = t('help_button');
    document.getElementById('controls-title')!.textContent = t('controls_title');
    const list = document.getElementById('controls-list')!;
    for (const key of ['ctrl_move', 'ctrl_look', 'ctrl_sprint', 'ctrl_interact', 'ctrl_map', 'ctrl_view', 'ctrl_zoom']) {
      const li = document.createElement('li');
      li.textContent = t(key);
      list.appendChild(li);
    }
    this.helpBtn.addEventListener('click', () => {
      const open = this.controlsPanel.style.display === 'block';
      this.controlsPanel.style.display = open ? 'none' : 'block';
    });
    this.controlsPanel.addEventListener('click', () => {
      this.controlsPanel.style.display = 'none';
    });

    document.getElementById('zoom-in')!.addEventListener('click', () => this.onZoomPressed?.(1));
    document.getElementById('zoom-out')!.addEventListener('click', () => this.onZoomPressed?.(-1));
    this.muteBtn.addEventListener('click', () => this.onMutePressed?.());
  }

  setMuted(muted: boolean): void {
    this.muteBtn.textContent = muted ? '🔇' : '🔊';
  }

  setViewMode(mode: 'first' | 'third' | 'heli'): void {
    this.viewBtn.textContent = t(mode === 'first' ? 'view_first' : mode === 'third' ? 'view_third' : 'view_heli');
  }

  private xpText = document.getElementById('xp-text')!;
  private xpFill = document.getElementById('xp-fill') as HTMLDivElement;
  private levelToast = document.getElementById('level-toast')!;
  private lastLevel: number | null = null;
  private levelToastTimer: number | null = null;

  onLevelUp: ((level: number) => void) | null = null;

  setDistrict(name: string): void {
    this.districtChip.textContent = name;
  }

  setProfile(profile: XpProfile): void {
    this.xpText.textContent = `${t('level_label', profile.level)} · ${profile.currentXp}/${profile.nextLevelXp} XP`;
    this.xpFill.style.width = `${Math.min(100, (profile.currentXp / profile.nextLevelXp) * 100)}%`;
    if (this.lastLevel !== null && profile.level > this.lastLevel) {
      this.showLevelToast(profile.level);
      this.onLevelUp?.(profile.level);
    }
    this.lastLevel = profile.level;
  }

  private showLevelToast(level: number): void {
    this.levelToast.textContent = t('level_up', level);
    this.levelToast.classList.remove('show');
    void (this.levelToast as HTMLElement).offsetWidth; // restart the CSS animation
    this.levelToast.classList.add('show');
    if (this.levelToastTimer) window.clearTimeout(this.levelToastTimer);
    this.levelToastTimer = window.setTimeout(() => this.levelToast.classList.remove('show'), 2500);
  }

  showPrompt(actionText: string): void {
    this.prompt.textContent = t('interact_hint', actionText);
    this.prompt.style.display = 'block';
    this.interactBtn.style.display = document.body.classList.contains('touch') ? 'block' : 'none';
  }

  hidePrompt(): void {
    this.prompt.style.display = 'none';
    this.interactBtn.style.display = 'none';
  }

  showXpToast(amount: number): void {
    this.toast.textContent = t('xp_gain', amount);
    this.toast.style.display = 'block';
    this.toast.style.opacity = '1';
    if (this.toastTimer) {
      window.clearTimeout(this.toastTimer);
    }
    this.toastTimer = window.setTimeout(() => {
      this.toast.style.display = 'none';
    }, 2000);
  }

  /** PoC measurement gate: live FPS counter (Blueprint §8). */
  tickFps(dt: number): void {
    this.frames += 1;
    this.fpsTimer += dt;
    if (this.fpsTimer >= 1) {
      this.perfChip.textContent = `${Math.round(this.frames / this.fpsTimer)} fps`;
      this.frames = 0;
      this.fpsTimer = 0;
    }
  }
}

/** Left-half virtual joystick + right-half look area for touch devices. */
export function createTouchControls(): TouchInput | null {
  if (!isTouchDevice) {
    return null;
  }
  document.body.classList.add('touch');

  const zone = document.getElementById('joystick-zone')!;
  const base = document.getElementById('joystick-base')!;
  const knob = document.getElementById('joystick-knob')!;
  const lookZone = document.getElementById('look-zone')!;

  const axis = { x: 0, y: 0 };
  let joyOrigin: { x: number; y: number } | null = null;
  const RADIUS = 55;

  zone.addEventListener('touchstart', (e) => {
    const touch = e.changedTouches[0];
    joyOrigin = { x: touch.clientX, y: touch.clientY };
    base.style.display = 'block';
    knob.style.display = 'block';
    positionEl(base, touch.clientX, touch.clientY, 55);
    positionEl(knob, touch.clientX, touch.clientY, 24);
    e.preventDefault();
  });
  zone.addEventListener('touchmove', (e) => {
    if (!joyOrigin) return;
    const touch = e.changedTouches[0];
    let dx = touch.clientX - joyOrigin.x;
    let dy = touch.clientY - joyOrigin.y;
    const len = Math.hypot(dx, dy);
    if (len > RADIUS) {
      dx = (dx / len) * RADIUS;
      dy = (dy / len) * RADIUS;
    }
    axis.x = dx / RADIUS;
    axis.y = dy / RADIUS;
    positionEl(knob, joyOrigin.x + dx, joyOrigin.y + dy, 24);
    e.preventDefault();
  });
  const endJoy = () => {
    joyOrigin = null;
    axis.x = 0;
    axis.y = 0;
    base.style.display = 'none';
    knob.style.display = 'none';
  };
  zone.addEventListener('touchend', endJoy);
  zone.addEventListener('touchcancel', endJoy);

  const lookDelta = { x: 0, y: 0 };
  let lastLook: { x: number; y: number } | null = null;
  lookZone.addEventListener('touchstart', (e) => {
    const touch = e.changedTouches[0];
    lastLook = { x: touch.clientX, y: touch.clientY };
    e.preventDefault();
  });
  lookZone.addEventListener('touchmove', (e) => {
    if (!lastLook) return;
    const touch = e.changedTouches[0];
    lookDelta.x += touch.clientX - lastLook.x;
    lookDelta.y += touch.clientY - lastLook.y;
    lastLook = { x: touch.clientX, y: touch.clientY };
    e.preventDefault();
  });
  const endLook = () => {
    lastLook = null;
  };
  lookZone.addEventListener('touchend', endLook);
  lookZone.addEventListener('touchcancel', endLook);

  return {
    getAxis: () => ({ x: axis.x, y: axis.y }),
    consumeLookDelta: () => {
      const d = { x: lookDelta.x, y: lookDelta.y };
      lookDelta.x = 0;
      lookDelta.y = 0;
      return d;
    },
  };
}

function positionEl(el: HTMLElement, cx: number, cy: number, half: number): void {
  el.style.left = `${cx - half}px`;
  el.style.top = `${cy - half}px`;
}
